import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Pages that are allowed to have dark mode
const DARK_MODE_ALLOWED_PAGES = [
    '/new-client-dashboard',
    '/new-my-team',
    '/new-client-ordered-report'
];

/**
 * Custom hook to manage dark mode state with page restrictions
 * Only allows dark mode on specific dashboard pages
 */
export const useDarkMode = () => {
    const location = useLocation();

    const [isDark, setIsDark] = useState(() => {
        // Check if current page allows dark mode
        const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);
        if (!isAllowedPage) return false;

        // Get saved preference for allowed pages
        try {
            const saved = localStorage.getItem('bpoh-new-dashboard-theme');
            if (saved === 'dark') return true;
            if (saved === 'light') return false;
        } catch (_) { }

        // Fall back to system preference
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    });

    const toggleTheme = () => {
        const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);
        if (!isAllowedPage) return; // Don't allow toggle on non-dashboard pages

        setIsDark((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('bpoh-new-dashboard-theme', next ? 'dark' : 'light');
                // Dispatch custom event to notify all components
                window.dispatchEvent(new CustomEvent('dark-mode-changed', {
                    detail: { isDark: next, isAllowedPage }
                }));
            } catch (_) { }
            return next;
        });
    };

    // Effect to listen for theme changes from other components
    useEffect(() => {
        const handleThemeChange = (event) => {
            const { isDark: newIsDark, isAllowedPage } = event.detail;
            const currentIsAllowed = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);

            if (currentIsAllowed) {
                setIsDark(newIsDark);
            }
        };

        window.addEventListener('dark-mode-changed', handleThemeChange);
        return () => window.removeEventListener('dark-mode-changed', handleThemeChange);
    }, [location.pathname]);

    // Effect to handle page changes
    useEffect(() => {
        const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);

        if (!isAllowedPage) {
            // Force light mode on non-dashboard pages
            setIsDark(false);
        } else {
            // Restore saved preference for dashboard pages
            try {
                const saved = localStorage.getItem('bpoh-new-dashboard-theme');
                if (saved === 'dark') {
                    setIsDark(true);
                } else if (saved === 'light') {
                    setIsDark(false);
                } else {
                    // Use system preference if no saved preference
                    if (typeof window !== 'undefined' && window.matchMedia) {
                        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
                    }
                }
            } catch (_) { }
        }
    }, [location.pathname]);

    // Effect to sync with document
    useEffect(() => {
        const root = document.documentElement;
        const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);
        const effectiveIsDark = isDark && isAllowedPage;

        root.style.colorScheme = effectiveIsDark ? 'dark' : 'light';
        root.dataset.bpohDashboardTheme = effectiveIsDark ? 'dark' : 'light';
    }, [isDark, location.pathname]);

    // Effect to listen to system preference changes (only for allowed pages)
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
            const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);
            const saved = localStorage.getItem('bpoh-new-dashboard-theme');

            // Only follow system preference if no saved preference and on allowed page
            if (!saved && isAllowedPage) {
                setIsDark(e.matches);
            }
        };

        if (media.addEventListener) media.addEventListener('change', handler);
        return () => {
            if (media.removeEventListener) media.removeEventListener('change', handler);
        };
    }, [location.pathname]);

    const isAllowedPage = DARK_MODE_ALLOWED_PAGES.includes(location.pathname);

    return {
        isDark: isDark && isAllowedPage, // Only return true if both dark AND on allowed page
        toggleTheme,
        isAllowedPage
    };
};